import {users as usersShared} from '@cl/shared';
import {KnowledgeRagTopic} from 'common/src/modules/ai/ai.enums';
import {BreadcrumbName} from 'common/src/modules/breadcrumbs/breadcrumbs.enums';
import checkWidgetExistsTool from 'common/src/modules/analytics/copilot/agents/campaignAnalyticsAgent/tools/checkWidgetExistsTool';
import createDashboardTool from 'common/src/modules/analytics/copilot/agents/campaignAnalyticsAgent/tools/createDashboardTool';
import createWidgetTool from 'common/src/modules/analytics/copilot/agents/campaignAnalyticsAgent/tools/createWidgetTool';
import deleteDashboardTool from 'common/src/modules/analytics/copilot/agents/campaignAnalyticsAgent/tools/deleteDashboardTool';
import deleteWidgetTool from 'common/src/modules/analytics/copilot/agents/campaignAnalyticsAgent/tools/deleteWidgetFromDashboardTool';
import getCampaignAnalyticsDataTool from 'common/src/modules/analytics/copilot/agents/campaignAnalyticsAgent/tools/getCampaignAnalyticsDataTool';
import getCampaignAnalyticsTableDataTool from 'common/src/modules/analytics/copilot/agents/campaignAnalyticsAgent/tools/getCampaignAnalyticsTableDataTool';
import getCampaignTopPerformingTool from 'common/src/modules/analytics/copilot/agents/campaignAnalyticsAgent/tools/getCampaignTopPerformingTool';
import getCampaignWorstPerformingTool from 'common/src/modules/analytics/copilot/agents/campaignAnalyticsAgent/tools/getCampaignWorstPerformingTool';
import highlightWidgetTool from 'common/src/modules/analytics/copilot/agents/campaignAnalyticsAgent/tools/highlightWidgetTool';
import navigateToCampaignInsightsTool from 'common/src/modules/analytics/copilot/agents/campaignAnalyticsAgent/tools/navigateToCampaignInsightsTool';
import nodeFallbackAnalyzerTool from 'common/src/modules/analytics/copilot/agents/campaignAnalyticsAgent/tools/nodeFallbackAnalyzerTool';
import openCampaignDashboardTool from 'common/src/modules/analytics/copilot/agents/campaignAnalyticsAgent/tools/openCampaignDashboardTool';
import renameDashboardTool from 'common/src/modules/analytics/copilot/agents/campaignAnalyticsAgent/tools/renameDashboardTool';
import supportedFilterValuesTool from 'common/src/modules/analytics/copilot/agents/campaignAnalyticsAgent/tools/supportedFilterValuesTool';
import updateWidgetTool from 'common/src/modules/analytics/copilot/agents/campaignAnalyticsAgent/tools/updateWidgetTool';
import persistInlineWidgetToCampaignDashboardTool from 'common/src/modules/analytics/copilot/agents/campaignAnalyticsAgent/tools/persistInlineWidgetToCampaignDashboardTool';
import getBestPracticesTool from 'common/src/modules/analytics/copilot/tools/getBestPracticesTool';
import {doesUserHaveRole} from 'common/src/modules/roles/utils/doesUserHaveRole';
import {RoleTypeName} from 'common/src/modules/users/users.types';
import {SkillName} from 'common/src/modules/shared/copilot/skills/skills.enums';
import skill from 'common/src/modules/shared/copilot/utils/creators/skill';
import {CLStore} from 'common/src/redux/store';

function campaignAnalyticsSkill(store: CLStore) {
  const userHasAnalyticsWriteRole = doesUserHaveRole(
    usersShared.userSubject.data,
    RoleTypeName.AnalyticsWrite,
  );

  const dashboardManagementTools = [
    createDashboardTool(store),
    renameDashboardTool(store),
    deleteDashboardTool(store),
    createWidgetTool(store),
    updateWidgetTool(store),
    deleteWidgetTool(store),
  ];

  return skill({
    name: SkillName.CampaignAnalytics,
    description: 'Fetches and analyzes campaign analytics data, manages dashboards and widgets',
    whenToUse:
      'Campaign-level analytics questions, performance metrics, and KPI reporting within a specific campaign. Handles fetching analytics data with metrics like CTR, conversion rate, impressions, and clicks. Can identify top and worst performing elements, provide best practices recommendations, manage campaign dashboards and widgets, and analyze node fallback data. Supports filtering by dimensions, date ranges, and campaign-specific attributes.',
    whenToAvoid:
      'Account-level or cross-campaign analytics — those belong to the account analytics skill.',
    instructions:
      'You are a campaign analytics specialist that fetches and analyzes campaign performance data, manages dashboards, and provides insights. You can retrieve analytics data with specific metrics, dimensions, date ranges, and filters. You can identify top and worst performing elements, provide best practices guidance, and analyze node fallback data. When presenting data, show inline widgets when possible and suggest adding useful widgets to dashboards. Only use metrics and dimensions from the available lists in the campaign context — never invent unsupported values.',
    tools: [
      navigateToCampaignInsightsTool(store),
      getCampaignTopPerformingTool(store),
      getCampaignAnalyticsDataTool(store),
      getCampaignAnalyticsTableDataTool(store),
      getCampaignWorstPerformingTool(store),
      getBestPracticesTool(),
      supportedFilterValuesTool(store),
      checkWidgetExistsTool(store),
      highlightWidgetTool(store),
      persistInlineWidgetToCampaignDashboardTool(store),
      openCampaignDashboardTool(store),
      nodeFallbackAnalyzerTool(store),
      ...(userHasAnalyticsWriteRole ? dashboardManagementTools : []),
    ],
    knowledgeTopics: [KnowledgeRagTopic.CampaignAnalyticsAgent],
    applicableBreadcrumbs: [BreadcrumbName.CampaignInsights],
  });
}

export default campaignAnalyticsSkill;
